import forge from "node-forge";
import { entra_reg_auth_domain, dashboard_id, dashboard_streams_exe_id } from "../../apiclients/apiurls.js";
const { pki, util: forgeUtil } = forge;
/**
 * Helper functions for Microsoft Entra ID integration
 */
const acsUrl = process.env.REACT_APP_ACS_URL;
export const generateMdata = async (siteid) => {
    try {
        const res = await fetch(`/api/entity-id/${siteid}`);
        if (!res.ok) {
            throw new Error(`API error ${res.status}: ${res.statusText}`);
        }

        const data = await res.json();
        const entity_id = data.entity_id;

        const requestedAttributes = ["email", "number", "departmentCode"];
        const attributesXml = requestedAttributes
            .map((attr) => `<md:RequestedAttribute Name="${attr}" />`)
            .join("\n      ");

        return `
<md:EntityDescriptor xmlns:md="urn:oasis:names:tc:SAML:2.0:metadata" entityID="${entity_id}">
  <md:SPSSODescriptor AuthnRequestsSigned="false" WantAssertionsSigned="false"
    protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol">
    <md:NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified</md:NameIDFormat>
    <md:AssertionConsumerService Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
      Location="${acsUrl}" index="0" isDefault="true" />
    <md:AttributeConsumingService index="0">
      <md:ServiceName xml:lang="en">AttributeContract</md:ServiceName>
      ${attributesXml}
    </md:AttributeConsumingService>
  </md:SPSSODescriptor>
</md:EntityDescriptor>
    `.trim();
    } catch (err) {
        console.error("Failed to generate metadata for siteid:", siteid, err);
        throw err;
    }
};

export const downloadMDoc = () => {
    const entity_id = entra_reg_auth_domain;
    const acsUrl = `${entra_reg_auth_domain}SSOResponse?dashboardid=${dashboard_streams_exe_id}&idp=mei`;

    const attributesXml = `
    <md:RequestedAttribute Name="email" isRequired="true" />
    <md:RequestedAttribute Name="firstName" isRequired="false" />
    <md:RequestedAttribute Name="lastName" isRequired="false" />  
  `.trim();

    const xmlContent = `
<md:EntityDescriptor xmlns:md="urn:oasis:names:tc:SAML:2.0:metadata" entityID="${entity_id}">
  <md:SPSSODescriptor AuthnRequestsSigned="false" WantAssertionsSigned="false"
    protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol">
    <md:NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified</md:NameIDFormat>
    <md:AssertionConsumerService Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
      Location="${acsUrl}" index="0" isDefault="true" />
    <md:AttributeConsumingService index="0">
      <md:ServiceName xml:lang="en">AttributeContract</md:ServiceName>
      ${attributesXml}
    </md:AttributeConsumingService>
  </md:SPSSODescriptor>
</md:EntityDescriptor>
`.trim();

    const blob = new Blob([xmlContent], { type: "application/xml" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "streams_entra_metadata.xml";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};



export const parseXMLDoc = async (event, setFormValues, formValues) => {
    const file = event.target.files[0];

    if (
        !file ||
        (!file.name.toLowerCase().endsWith(".xml") &&
            !file.name.toLowerCase().endsWith(".cer"))
    ) {
        alert("Please upload a valid .xml or .cer file");
        return;
    }

    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = function (event) {
            try {
                const fileText = event.target.result;
                let parsedData = {};
                let base64Cert = "";
                let expiryDate = "";

                // Helper function to format date as YYYY-MM-DD HH:mm:ss
                const formatDateToYYYYMMDD_HHMMSS = (date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    const hours = String(date.getHours()).padStart(2, "0");
                    const minutes = String(date.getMinutes()).padStart(2, "0");
                    const seconds = String(date.getSeconds()).padStart(2, "0");
                    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                };

                if (file.name.toLowerCase().endsWith(".xml")) {
                    // XML logic
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(fileText, "text/xml");

                    const certNode = xmlDoc.getElementsByTagName("X509Certificate")[0];
                    base64Cert = certNode?.textContent?.trim() || "";

                    if (!base64Cert) {
                        alert("Certificate not found in the XML file.");
                        return;
                    }

                    // Convert base64 → PEM
                    const pemCert = `-----BEGIN CERTIFICATE-----\n${base64Cert
                        .match(/.{1,64}/g)
                        .join("\n")}\n-----END CERTIFICATE-----`;

                    const cert = pki.certificateFromPem(pemCert);
                    // Format the expiry date to YYYY-MM-DD HH:mm:ss
                    expiryDate = formatDateToYYYYMMDD_HHMMSS(cert.validity.notAfter);

                    const entityID = xmlDoc.documentElement.getAttribute("entityID") || "";
                    const tenantMatch = entityID.match(
                        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
                    );
                    const tenantID = tenantMatch ? tenantMatch[0] : "";

                    const ssoService = xmlDoc.getElementsByTagName("SingleSignOnService")[0];
                    const signinUrl = ssoService?.getAttribute("Location") || "";

                    const nameIdFormat =
                        xmlDoc.getElementsByTagName("NameIDFormat")[0]?.textContent || "";

                    const attributes = xmlDoc.getElementsByTagName("Attribute");
                    const claimTypes = Array.from(attributes)
                        .map((attr) => attr.getAttribute("Name") || "")
                        .filter(Boolean);

                    parsedData = {
                        entity_id: entityID,
                        tenant_id: tenantID,
                        signinurl: signinUrl,
                        nameIdFormat,
                        claimTypes: claimTypes.join(", "),
                        certificate: base64Cert,
                        ssoaccountid: "Microsoft Entra ID",
                        expirydate: expiryDate,
                    };
                } else if (file.name.toLowerCase().endsWith(".cer")) {
                    // CER logic
                    if (fileText.includes("-----BEGIN CERTIFICATE-----")) {
                        // PEM format
                        const cert = pki.certificateFromPem(fileText);
                        expiryDate = formatDateToYYYYMMDD_HHMMSS(cert.validity.notAfter);

                        // Extract raw base64 without headers
                        base64Cert = fileText
                            .replace("-----BEGIN CERTIFICATE-----", "")
                            .replace("-----END CERTIFICATE-----", "")
                            .replace(/\r?\n|\r/g, "")
                            .trim();
                    } else {
                        // DER (binary) → Base64
                        const binary = event.target.result;
                        const base64Der = forgeUtil.encode64(binary);
                        const pemCert = `-----BEGIN CERTIFICATE-----\n${base64Der
                            .match(/.{1,64}/g)
                            .join("\n")}\n-----END CERTIFICATE-----`;

                        const cert = pki.certificateFromPem(pemCert);
                        expiryDate = formatDateToYYYYMMDD_HHMMSS(cert.validity.notAfter);
                        base64Cert = base64Der;
                    }

                    parsedData = {
                        certificate: base64Cert,
                        expirydate: expiryDate,
                    };
                }

                console.log("Parsed SSO Data:", parsedData);

                setFormValues((prev) => ({
                    ...prev,
                    ...parsedData,
                }));

                resolve(parsedData);
            } catch (err) {
                console.error("Failed to parse file:", err);
                alert("Invalid file or certificate format.");
            }
        };


        reader.onerror = () => {
            console.error("File reading failed.");
            alert("File reading error");
        };

        //  For `.cer` in binary mode, use `readAsArrayBuffer`
        if (file.name.toLowerCase().endsWith(".cer")) {
            reader.readAsBinaryString(file);
        } else {
            reader.readAsText(file);
        }
    });
};



